import { ErrorHandler } from '@angular/core';
import _rg4js from 'raygun4js';
const rg4js = _rg4js as any; // type defs don't cover all command strings (filterSensitiveData, send)

/**
 * HIPAA-compliant Error Handler for Raygun
 *
 * This error handler sanitizes error data before sending to Raygun to ensure
 * no Protected Health Information (PHI) is transmitted to third-party services.
 *
 * PHI patterns that are filtered:
 * - Patient names (firstname, lastname, clientname)
 * - Date of birth (dob, dateOfBirth, DateOfBirth)
 * - Social Security Numbers (ssn, socialSecurityNumber)
 * - Medical Record Numbers (mrn, medicalRecordNumber)
 * - Patient IDs that might contain identifiable information
 * - Email addresses
 * - Phone numbers
 * - Addresses
 */
export class RaygunErrorHandler implements ErrorHandler {
  // PHI field names to redact from error objects
  private static readonly PHI_FIELDS = [
    'firstname', 'firstName', 'first_name',
    'lastname', 'lastName', 'last_name',
    'clientname', 'clientName', 'client_name',
    'dob', 'dateOfBirth', 'DateOfBirth', 'date_of_birth', 'birthDate', 'birth_date',
    'ssn', 'socialSecurityNumber', 'social_security_number',
    'mrn', 'medicalRecordNumber', 'medical_record_number',
    'gender', 'sex',
    'placeOfBirth', 'place_of_birth', 'birthPlace',
    'email', 'emailAddress', 'email_address',
    'phone', 'phoneNumber', 'phone_number', 'cellphone', 'cell_phone',
    'address', 'streetAddress', 'street_address',
    'city', 'state', 'zip', 'zipCode', 'zip_code', 'postalCode',
    'password', 'token', 'accessToken', 'refreshToken',
    'patientName', 'patient_name'
  ];

  // Regex patterns to detect and redact PHI in string values
  private static readonly PHI_PATTERNS = [
    // SSN pattern (XXX-XX-XXXX or XXXXXXXXX)
    { pattern: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g, replacement: '[SSN_REDACTED]' },
    // Email pattern
    { pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, replacement: '[EMAIL_REDACTED]' },
    // Phone pattern (various formats)
    { pattern: /\b(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, replacement: '[PHONE_REDACTED]' },
    // Date of birth pattern (MM/DD/YYYY, YYYY-MM-DD, etc.)
    { pattern: /\b(0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12]\d|3[01])[\/\-](19|20)\d{2}\b/g, replacement: '[DOB_REDACTED]' },
    { pattern: /\b(19|20)\d{2}[\/\-](0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12]\d|3[01])\b/g, replacement: '[DOB_REDACTED]' }
  ];

  handleError(e: any): void {
    // Only send to Raygun in production
    if (window.location.hostname === 'medical.nuagedx.com') {
      try {
        // Sanitize the error before sending
        const sanitizedError = this.sanitizeError(e);

        // Configure Raygun to filter additional data
        rg4js('filterSensitiveData', RaygunErrorHandler.PHI_FIELDS);

        // Send sanitized error
        rg4js('send', { error: sanitizedError });
      } catch (raygunError) {
        // Silently fail - don't expose errors about error handling
      }
    }

    // Log to console in development (sanitized)
    if (window.location.hostname !== 'medical.nuagedx.com') {
      console.error('[Error]', this.sanitizeForConsole(e));
    }
  }

  /**
   * Sanitizes an error object by removing PHI from all properties
   */
  private sanitizeError(error: any): any {
    if (error === null || error === undefined) {
      return error;
    }

    // Handle Error objects
    if (error instanceof Error) {
      const sanitizedError = new Error(this.sanitizeString(error.message));
      sanitizedError.name = error.name;
      if (error.stack) {
        sanitizedError.stack = this.sanitizeString(error.stack);
      }
      return sanitizedError;
    }

    // Handle strings
    if (typeof error === 'string') {
      return this.sanitizeString(error);
    }

    // Handle objects (deep clone and sanitize)
    if (typeof error === 'object') {
      return this.sanitizeObject(error, new WeakSet());
    }

    return error;
  }

  /**
   * Recursively sanitizes an object by removing PHI fields and patterns
   */
  private sanitizeObject(obj: any, seen: WeakSet<any>): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Prevent circular reference issues
    if (seen.has(obj)) {
      return '[Circular Reference]';
    }

    if (typeof obj !== 'object') {
      return typeof obj === 'string' ? this.sanitizeString(obj) : obj;
    }

    seen.add(obj);

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, seen));
    }

    // Handle objects
    const sanitized: any = {};
    for (const key of Object.keys(obj)) {
      // Check if this is a PHI field
      if (this.isPHIField(key)) {
        sanitized[key] = '[PHI_REDACTED]';
      } else {
        const value = obj[key];
        if (typeof value === 'string') {
          sanitized[key] = this.sanitizeString(value);
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitizeObject(value, seen);
        } else {
          sanitized[key] = value;
        }
      }
    }

    return sanitized;
  }

  /**
   * Checks if a field name is a known PHI field
   */
  private isPHIField(fieldName: string): boolean {
    const lowerFieldName = fieldName.toLowerCase();
    return RaygunErrorHandler.PHI_FIELDS.some(
      phiField => lowerFieldName === phiField.toLowerCase()
    );
  }

  /**
   * Sanitizes a string by replacing PHI patterns with redaction markers
   */
  private sanitizeString(str: string): string {
    if (!str || typeof str !== 'string') {
      return str;
    }

    let sanitized = str;
    for (const { pattern, replacement } of RaygunErrorHandler.PHI_PATTERNS) {
      sanitized = sanitized.replace(pattern, replacement);
    }

    return sanitized;
  }

  /**
   * Sanitizes error for console output (less aggressive than Raygun)
   */
  private sanitizeForConsole(error: any): string {
    if (error instanceof Error) {
      return `${error.name}: ${this.sanitizeString(error.message)}`;
    }
    if (typeof error === 'string') {
      return this.sanitizeString(error);
    }
    return '[Error object - details redacted for HIPAA compliance]';
  }

  /**
   * Static list of PHI fields for external access (e.g., Raygun filterSensitiveData)
   */
  static get phiFields(): string[] {
    return [...RaygunErrorHandler.PHI_FIELDS];
  }
}
